import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutGrid, Package, GraduationCap, ShoppingBag, Ticket, LogOut,
  Plus, Trash2, Upload, X, Image as ImageIcon, Pencil, Images,
  FileText, Save, ArrowUp, ArrowDown, Navigation, Mail,
} from "lucide-react";
import { api, imgUrl, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const CATEGORIES = ["Original Artwork", "Fine Art Prints", "Books", "Educational Products", "Merchandise"];
const inputCls = "w-full bg-ivory border border-matte/15 px-4 py-2.5 text-sm focus:outline-none focus:border-matte transition-colors";

function ImageUploader({ images, setImages }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef();

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd);
      setImages((prev) => [...prev, r.data.url]);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3">
        {images.map((img, i) => (
          <div key={img} className="relative w-20 h-20 bg-cream overflow-hidden group">
            <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => setImages(images.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-matte/70 text-ivory p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <X size={12} />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="w-20 h-20 border border-dashed border-matte/30 flex items-center justify-center text-matte/40 hover:border-matte transition-colors" data-testid="upload-image-btn">
          {busy ? "…" : <Upload size={18} />}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      </div>
    </div>
  );
}

function ProductForm({ onSaved, onCancel, onDelete, initial }) {
  const defaults = { title: "", category: CATEGORIES[0], description: "", price: "", inventory: "", featured: false, images: [] };
  const [form, setForm] = useState({ ...defaults, ...(initial || {}) });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title || form.price === "" || form.price === null || form.price === undefined) {
      toast.error("Title and price are required");
      return;
    }
    const payload = { ...form, price: parseFloat(form.price), inventory: parseInt(form.inventory || 0) };
    try {
      if (initial?.id) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved");
      onSaved();
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <div className="space-y-4">
      <input placeholder="Title" value={form.title} onChange={set("title")} className={inputCls} data-testid="product-form-title" />
      <select value={form.category} onChange={set("category")} className={inputCls} data-testid="product-form-category">
        {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
      </select>
      <textarea placeholder="Description" rows={3} value={form.description} onChange={set("description")} className={`${inputCls} resize-none`} data-testid="product-form-desc" />
      <div className="grid grid-cols-2 gap-4">
        <input type="number" step="0.01" placeholder="Price" value={form.price} onChange={set("price")} className={inputCls} data-testid="product-form-price" />
        <input type="number" placeholder="Inventory" value={form.inventory} onChange={set("inventory")} className={inputCls} data-testid="product-form-inventory" />
      </div>
      <label className="flex items-center gap-2 text-sm text-matte/70">
        <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-plum" />
        Featured on homepage
      </label>
      <div>
        <p className="text-[11px] uppercase tracking-wide-sm text-matte/50 mb-2">Images</p>
        <ImageUploader images={form.images} setImages={(updater) => setForm((f) => ({ ...f, images: typeof updater === "function" ? updater(f.images) : updater }))} />
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={save} className="flex-1 bg-matte text-ivory py-3 text-[12px] uppercase tracking-editorial hover:bg-plum transition-colors" data-testid="product-form-save">Save</button>
        {onCancel && <button type="button" onClick={onCancel} className="px-6 py-3 border border-matte/30 text-[12px] uppercase tracking-editorial hover:bg-cream transition-colors" data-testid="product-form-cancel">Cancel</button>}
        {initial?.id && onDelete && <button type="button" onClick={() => onDelete(initial.id)} className="px-6 py-3 border border-destructive/40 text-destructive text-[12px] uppercase tracking-editorial hover:bg-destructive hover:text-ivory transition-colors" data-testid="product-form-delete">Delete</button>}
      </div>
    </div>
  );
}

function ProgramForm({ onSaved, onCancel, onDelete, initial }) {
  const defaults = { title: "", subtitle: "", description: "", full_description: "", ages: "3–5", duration: "Four Weeks", days: "Tuesdays & Thursdays", start_time: "", end_time: "", start_date: "", end_date: "", price: "", max_enrollment: "", instructor: "", status: "active", schedule: "", image: "" };
  const [form, setForm] = useState({ ...defaults, ...(initial || {}) });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.title || form.price === "" || form.price === null || form.price === undefined) {
      toast.error("Title and price are required");
      return;
    }
    const schedule = form.days || form.schedule;
    const payload = { ...form, price: parseFloat(form.price), max_enrollment: parseInt(form.max_enrollment || 0), schedule };
    try {
      if (initial?.id) await api.put(`/admin/programs/${initial.id}`, payload);
      else await api.post("/admin/programs", payload);
      toast.success("Program saved");
      onSaved();
    } catch { toast.error("Save failed"); }
  };
  return (
    <div className="space-y-4">
      <input placeholder="Class title" value={form.title} onChange={set("title")} className={inputCls} data-testid="program-form-title" />
      <input placeholder="Session / subtitle (optional)" value={form.subtitle} onChange={set("subtitle")} className={inputCls} data-testid="program-form-subtitle" />
      <textarea placeholder="Short description" rows={2} value={form.description} onChange={set("description")} className={`${inputCls} resize-none`} data-testid="program-form-desc" />
      <textarea placeholder="Full description" rows={4} value={form.full_description} onChange={set("full_description")} className={`${inputCls} resize-none`} data-testid="program-form-full-desc" />
      <div className="grid grid-cols-2 gap-4">
        <input placeholder="Ages" value={form.ages} onChange={set("ages")} className={inputCls} data-testid="program-form-ages" />
        <input placeholder="Days" value={form.days} onChange={set("days")} className={inputCls} data-testid="program-form-days" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-[10px] uppercase tracking-wide-sm text-matte/40">Start time</label><input type="time" value={form.start_time} onChange={set("start_time")} className={inputCls} data-testid="program-form-start-time" /></div>
        <div><label className="text-[10px] uppercase tracking-wide-sm text-matte/40">End time</label><input type="time" value={form.end_time} onChange={set("end_time")} className={inputCls} data-testid="program-form-end-time" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-[10px] uppercase tracking-wide-sm text-matte/40">Start date</label><input type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} data-testid="program-form-start-date" /></div>
        <div><label className="text-[10px] uppercase tracking-wide-sm text-matte/40">End date</label><input type="date" value={form.end_date} onChange={set("end_date")} className={inputCls} data-testid="program-form-end-date" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <input type="number" step="0.01" placeholder="Price" value={form.price} onChange={set("price")} className={inputCls} data-testid="program-form-price" />
        <input type="number" placeholder="Maximum enrollment" value={form.max_enrollment} onChange={set("max_enrollment")} className={inputCls} data-testid="program-form-max" />
      </div>
      <input placeholder="Instructor (optional)" value={form.instructor} onChange={set("instructor")} className={inputCls} data-testid="program-form-instructor" />
      <select value={form.status} onChange={set("status")} className={inputCls} data-testid="program-form-status">
        <option value="active">Active</option>
        <option value="draft">Draft</option>
        <option value="hidden">Hidden</option>
      </select>
      <div>
        <p className="text-[11px] uppercase tracking-wide-sm text-matte/50 mb-2">Class Photo</p>
        <ImageUploader
          images={form.image ? [form.image] : []}
          setImages={(updater) => setForm((f) => {
            const cur = f.image ? [f.image] : [];
            const arr = typeof updater === "function" ? updater(cur) : updater;
            return { ...f, image: arr.length ? arr[arr.length - 1] : "" };
          })}
        />
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={save} className="flex-1 bg-matte text-ivory py-3 text-[12px] uppercase tracking-editorial hover:bg-plum transition-colors" data-testid="program-form-save">Save</button>
        {onCancel && <button type="button" onClick={onCancel} className="px-6 py-3 border border-matte/30 text-[12px] uppercase tracking-editorial hover:bg-cream transition-colors" data-testid="program-form-cancel">Cancel</button>}
        {initial?.id && onDelete && <button type="button" onClick={() => onDelete(initial.id)} className="px-6 py-3 border border-destructive/40 text-destructive text-[12px] uppercase tracking-editorial hover:bg-destructive hover:text-ivory transition-colors" data-testid="program-form-delete">Delete</button>}
      </div>
    </div>
  );
}

function SiteMediaSlot({ slot, onSaved }) {
  const [meta, setMeta] = useState({
    alt: slot.alt || "", caption: slot.caption || "", credit: slot.credit || "",
    show_caption: !!slot.show_caption, focal: slot.focal || "center", overlay: slot.overlay ?? 45,
  });
  const [open, setOpen] = useState(false);
  const saveMeta = async () => {
    try {
      await api.put(`/admin/site-media/${slot.key}`, { url: slot.url, ...meta });
      toast.success("Details saved");
      onSaved();
    } catch { toast.error("Update failed"); }
  };
  return (
    <div className="border border-matte/10 p-4 bg-cream" data-testid={`sitemedia-${slot.key}`}>
      <p className="text-[11px] uppercase tracking-wide-sm text-matte/50 mb-3">{slot.label}</p>
      <ImageUploader
        images={slot.url ? [slot.url] : []}
        setImages={async (updater) => {
          const cur = slot.url ? [slot.url] : [];
          const arr = typeof updater === "function" ? updater(cur) : updater;
          const url = arr.length ? arr[arr.length - 1] : "";
          try {
            await api.put(`/admin/site-media/${slot.key}`, { url, ...meta });
            toast.success(url ? "Page photo updated" : "Reset to default");
            onSaved();
          } catch { toast.error("Update failed"); }
        }}
      />
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-[11px] uppercase tracking-wide-sm text-plum hover:text-matte mt-1" data-testid={`sitemedia-toggle-${slot.key}`}>
        {open ? "Hide options" : "Caption, alt & position"}
      </button>
      {open && (
        <div className="space-y-3 mt-3">
          <input placeholder="Alt text (accessibility)" value={meta.alt} onChange={(e) => setMeta({ ...meta, alt: e.target.value })} className={inputCls} data-testid={`sitemedia-alt-${slot.key}`} />
          <input placeholder="Caption (optional)" value={meta.caption} onChange={(e) => setMeta({ ...meta, caption: e.target.value })} className={inputCls} />
          <input placeholder="Photo credit (optional)" value={meta.credit} onChange={(e) => setMeta({ ...meta, credit: e.target.value })} className={inputCls} />
          <label className="flex items-center gap-2 text-sm text-matte/70">
            <input type="checkbox" checked={meta.show_caption} onChange={(e) => setMeta({ ...meta, show_caption: e.target.checked })} className="accent-plum" /> Show caption on site
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wide-sm text-matte/40">Focal point</label>
              <select value={meta.focal} onChange={(e) => setMeta({ ...meta, focal: e.target.value })} className={inputCls}>
                {["center", "top", "bottom", "left", "right", "top left", "top right", "bottom left", "bottom right"].map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide-sm text-matte/40">Overlay darkness ({meta.overlay}%)</label>
              <input type="range" min="0" max="90" value={meta.overlay} onChange={(e) => setMeta({ ...meta, overlay: parseInt(e.target.value) })} className="w-full accent-plum mt-3" />
            </div>
          </div>
          <button type="button" onClick={saveMeta} className="bg-matte text-ivory px-5 py-2 text-[11px] uppercase tracking-wide-sm hover:bg-plum transition-colors" data-testid={`sitemedia-save-${slot.key}`}>Save details</button>
        </div>
      )}
    </div>
  );
}

function ContentManager({ groups, onSaved }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const init = {};
    (groups || []).forEach((g) => g.fields.forEach((f) => { init[f.key] = f.value; }));
    setValues(init);
  }, [groups]);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/site-content", { values }); toast.success("Content saved & published"); onSaved(); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-2xl">Site Content</h2>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-matte text-ivory px-6 py-2.5 text-[11px] uppercase tracking-wide-sm hover:bg-plum transition-colors disabled:opacity-50" data-testid="content-save-btn"><Save size={14} /> {saving ? "Saving…" : "Save & Publish"}</button>
      </div>
      <p className="text-sm text-matte/50 mb-8 max-w-2xl">Edit wording across the site. Leave any field blank to hide it. Changes publish to the live site immediately after saving.</p>
      <div className="space-y-8">
        {(groups || []).map((g) => (
          <div key={g.group} className="border border-matte/10 bg-cream p-6" data-testid={`content-group-${g.group.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-[11px] uppercase tracking-editorial text-plum mb-5">{g.group}</p>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
              {g.fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <label className="text-[11px] uppercase tracking-wide-sm text-matte/50 mb-1.5 block">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={3} value={values[f.key] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className={`${inputCls} resize-none`} data-testid={`content-${f.key}`} />
                  ) : (
                    <input value={values[f.key] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className={inputCls} data-testid={`content-${f.key}`} placeholder={f.type === "link" ? "/path or https://…" : ""} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NavManager({ items, onSaved }) {
  const [list, setList] = useState(items || []);
  useEffect(() => setList(items || []), [items]);
  const upd = (i, k, val) => setList((l) => l.map((n, x) => (x === i ? { ...n, [k]: val } : n)));
  const move = (i, d) => setList((l) => { const a = [...l]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const add = () => setList((l) => [...l, { id: `nav-${Date.now()}`, label: "New Link", to: "/", external: false, visible: true }]);
  const remove = (i) => setList((l) => l.filter((_, x) => x !== i));
  const save = async () => { try { await api.put("/admin/nav", { items: list }); toast.success("Navigation saved & published"); onSaved(); } catch { toast.error("Save failed"); } };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-2xl">Navigation</h2>
        <div className="flex gap-3">
          <button onClick={add} className="flex items-center gap-2 border border-matte/30 px-5 py-2.5 text-[11px] uppercase tracking-wide-sm hover:bg-cream transition-colors" data-testid="nav-add-btn"><Plus size={14} /> Add</button>
          <button onClick={save} className="flex items-center gap-2 bg-matte text-ivory px-6 py-2.5 text-[11px] uppercase tracking-wide-sm hover:bg-plum transition-colors" data-testid="nav-save-btn"><Save size={14} /> Save & Publish</button>
        </div>
      </div>
      <p className="text-sm text-matte/50 mb-8 max-w-2xl">Manage the top navigation. Reorder, rename, hide, or link items (internal path or external URL).</p>
      <div className="space-y-3">
        {list.map((n, i) => (
          <div key={n.id || i} className="border border-matte/10 bg-cream p-4 grid md:grid-cols-12 gap-3 items-center" data-testid={`nav-item-${i}`}>
            <input value={n.label} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Label" className={`${inputCls} md:col-span-3`} data-testid={`nav-label-${i}`} />
            <input value={n.to} onChange={(e) => upd(i, "to", e.target.value)} placeholder="/path or https://…" className={`${inputCls} md:col-span-4`} data-testid={`nav-to-${i}`} />
            <label className="flex items-center gap-2 text-xs text-matte/70 md:col-span-2"><input type="checkbox" checked={!!n.external} onChange={(e) => upd(i, "external", e.target.checked)} className="accent-plum" /> External</label>
            <label className="flex items-center gap-2 text-xs text-matte/70 md:col-span-1"><input type="checkbox" checked={n.visible !== false} onChange={(e) => upd(i, "visible", e.target.checked)} className="accent-plum" /> Show</label>
            <div className="flex gap-1 md:col-span-2 justify-end">
              <button onClick={() => move(i, -1)} className="p-2 text-matte/40 hover:text-matte" data-testid={`nav-up-${i}`}><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} className="p-2 text-matte/40 hover:text-matte" data-testid={`nav-down-${i}`}><ArrowDown size={15} /></button>
              <button onClick={() => remove(i)} className="p-2 text-matte/40 hover:text-destructive" data-testid={`nav-remove-${i}`}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [orders, setOrders] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [dcode, setDcode] = useState("");
  const [dpct, setDpct] = useState(10);
  const [pdOpen, setPdOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [pgOpen, setPgOpen] = useState(false);
  const [editProgram, setEditProgram] = useState(null);
  const [siteMedia, setSiteMedia] = useState([]);
  const [siteContent, setSiteContent] = useState([]);
  const [nav, setNav] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/admin/login"); return; }
    if (!user.is_admin) { toast.error("Admin access required"); navigate("/"); return; }
    refresh();
    // eslint-disable-next-line
  }, [user, loading]);

  const refresh = async () => {
    try {
      const [s, p, pg, o, e, d, sm, sc, nv, cm, ns] = await Promise.all([
        api.get("/admin/stats"), api.get("/products"), api.get("/admin/programs"),
        api.get("/admin/orders"), api.get("/admin/enrollments"), api.get("/admin/discounts"),
        api.get("/admin/site-media"), api.get("/admin/site-content"), api.get("/admin/nav"),
        api.get("/admin/contacts"), api.get("/admin/newsletter"),
      ]);
      setStats(s.data); setProducts(p.data); setPrograms(pg.data);
      setOrders(o.data); setEnrollments(e.data); setDiscounts(d.data); setSiteMedia(sm.data);
      setSiteContent(sc.data); setNav(nv.data); setContacts(cm.data); setSubscribers(ns.data);
    } catch (err) {
      console.error("Failed to refresh admin dashboard data:", err);
    }
  };

  const delProduct = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Removed"); refresh(); };
  const delProgram = async (id) => { await api.delete(`/admin/programs/${id}`); toast.success("Removed"); refresh(); };
  const addDiscount = async () => {
    if (!dcode) return;
    try { await api.post("/admin/discounts", { code: dcode, percent_off: parseInt(dpct) }); setDcode(""); toast.success("Code created"); refresh(); }
    catch { toast.error("Failed"); }
  };
  const delDiscount = async (id) => { await api.delete(`/admin/discounts/${id}`); refresh(); };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center font-serif text-2xl text-matte/40">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="border-b border-matte/10 bg-ivory sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brainwaves-mark.png" alt="" className="h-9 w-auto object-contain" />
            <div>
              <p className="font-serif text-xl leading-none">Brainwaves</p>
              <p className="text-[10px] uppercase tracking-wide-sm text-matte/40">Studio Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-matte/60 hidden sm:inline">{user.name || user.email}</span>
            <button onClick={doLogout} className="flex items-center gap-2 text-[11px] uppercase tracking-wide-sm text-matte/60 hover:text-matte" data-testid="admin-logout-btn">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[["Revenue", money(stats.revenue)], ["Orders", stats.orders], ["Enrollments", stats.enrollments], ["Active Products", stats.products]].map(([label, val]) => (
              <div key={label} className="border border-matte/10 p-6 bg-cream">
                <p className="text-[10px] uppercase tracking-wide-sm text-matte/40 mb-2">{label}</p>
                <p className="font-serif text-3xl" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{val}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="products">
          <TabsList className="bg-transparent border-b border-matte/10 rounded-none w-full justify-start gap-2 h-auto p-0 mb-8 flex-wrap">
            {[["products", "Products", Package], ["programs", "Cohorts", GraduationCap], ["content", "Site Content", FileText], ["sitephotos", "Page Photos", Images], ["navigation", "Navigation", Navigation], ["messages", "Messages", Mail], ["orders", "Orders", ShoppingBag], ["enrollments", "Enrollments", LayoutGrid], ["discounts", "Discounts", Ticket]].map(([v, label, Icon]) => (
              <TabsTrigger key={v} value={v} className="data-[state=active]:bg-matte data-[state=active]:text-ivory rounded-none px-5 py-2.5 text-[11px] uppercase tracking-wide-sm" data-testid={`tab-${v}`}>
                <Icon size={14} className="mr-2" /> {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* PRODUCTS */}
          <TabsContent value="products">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">Products &amp; Artwork</h2>
              <Dialog open={pdOpen} onOpenChange={setPdOpen}>
                <DialogTrigger asChild>
                  <button className="flex items-center gap-2 bg-matte text-ivory px-5 py-2.5 text-[11px] uppercase tracking-wide-sm hover:bg-plum transition-colors" data-testid="add-product-btn"><Plus size={14} /> Add Product</button>
                </DialogTrigger>
                <DialogContent className="bg-ivory max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-serif text-2xl font-light">New Product</DialogTitle></DialogHeader>
                  <ProductForm onSaved={() => { setPdOpen(false); refresh(); }} />
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid gap-3">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 border border-matte/10 p-4 bg-cream" data-testid={`admin-product-${p.id}`}>
                  <div className="w-14 h-14 bg-ivory overflow-hidden flex-shrink-0">
                    {p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="m-auto mt-4 text-matte/30" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg truncate">{p.title} {p.featured && <span className="text-[10px] uppercase tracking-wide-sm text-brass ml-2">Featured</span>}</p>
                    <p className="text-xs text-matte/50">{p.category} · {money(p.price)} · Stock {p.inventory}</p>
                  </div>
                  <button onClick={() => setEditProduct(p)} className="text-matte/40 hover:text-matte p-2" data-testid={`edit-product-${p.id}`}><Pencil size={16} /></button>
                  <button onClick={() => delProduct(p.id)} className="text-matte/40 hover:text-destructive p-2" data-testid={`del-product-${p.id}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>

            <Dialog open={!!editProduct} onOpenChange={(o) => { if (!o) setEditProduct(null); }}>
              <DialogContent className="bg-ivory max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif text-2xl font-light">Edit Product</DialogTitle></DialogHeader>
                {editProduct && (
                  <ProductForm
                    initial={editProduct}
                    onSaved={() => { setEditProduct(null); refresh(); }}
                    onCancel={() => setEditProduct(null)}
                    onDelete={async (id) => { await delProduct(id); setEditProduct(null); }}
                  />
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* PROGRAMS */}
          <TabsContent value="programs">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">Cohorts &amp; Schedules</h2>
              <Dialog open={pgOpen} onOpenChange={setPgOpen}>
                <DialogTrigger asChild>
                  <button className="flex items-center gap-2 bg-matte text-ivory px-5 py-2.5 text-[11px] uppercase tracking-wide-sm hover:bg-plum transition-colors" data-testid="add-program-btn"><Plus size={14} /> Add Program</button>
                </DialogTrigger>
                <DialogContent className="bg-ivory max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-serif text-2xl font-light">New Program</DialogTitle></DialogHeader>
                  <ProgramForm onSaved={() => { setPgOpen(false); refresh(); }} onCancel={() => setPgOpen(false)} />
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid gap-3">
              {programs.map((p) => (
                <div key={p.id} className="flex items-center gap-4 border border-matte/10 p-4 bg-cream" data-testid={`admin-program-${p.id}`}>
                  <div className="w-14 h-14 bg-ivory overflow-hidden flex-shrink-0">
                    {p.image ? <img src={imgUrl(p.image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="m-auto mt-4 text-matte/30" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg truncate">{p.title}{p.subtitle ? ` · ${p.subtitle}` : ""} <span className="text-[10px] uppercase tracking-wide-sm text-brass ml-1">{p.status || (p.active ? "active" : "hidden")}</span></p>
                    <p className="text-xs text-matte/50">{p.schedule} · {money(p.price)} · Ages {p.ages}</p>
                  </div>
                  <button onClick={() => setEditProgram(p)} className="text-matte/40 hover:text-matte p-2" data-testid={`edit-program-${p.id}`}><Pencil size={16} /></button>
                  <button onClick={() => delProgram(p.id)} className="text-matte/40 hover:text-destructive p-2" data-testid={`del-program-${p.id}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>

            <Dialog open={!!editProgram} onOpenChange={(o) => { if (!o) setEditProgram(null); }}>
              <DialogContent className="bg-ivory max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif text-2xl font-light">Edit Program</DialogTitle></DialogHeader>
                {editProgram && (
                  <ProgramForm
                    initial={editProgram}
                    onSaved={() => { setEditProgram(null); refresh(); }}
                    onCancel={() => setEditProgram(null)}
                    onDelete={async (id) => { await delProgram(id); setEditProgram(null); }}
                  />
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* SITE CONTENT */}
          <TabsContent value="content">
            <ContentManager groups={siteContent} onSaved={refresh} />
          </TabsContent>

          {/* NAVIGATION */}
          <TabsContent value="navigation">
            <NavManager items={nav} onSaved={refresh} />
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages">
            <h2 className="font-serif text-2xl mb-2">Messages</h2>
            <p className="text-sm text-matte/50 mb-8 max-w-2xl">Contact-form messages and newsletter signups. A copy of every new message is also emailed to you.</p>
            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <p className="text-[11px] uppercase tracking-editorial text-plum mb-4">Contact messages ({contacts.length})</p>
                {contacts.length === 0 ? <p className="text-matte/40 text-sm" data-testid="no-contacts">No messages yet.</p> : (
                  <div className="space-y-4">
                    {contacts.map((msg) => (
                      <div key={msg.id} className="border border-matte/10 bg-cream p-4" data-testid={`contact-msg-${msg.id}`}>
                        <div className="flex justify-between items-baseline gap-3">
                          <p className="font-serif text-lg">{msg.name}</p>
                          <span className="text-[10px] uppercase tracking-wide-sm text-matte/40">{(msg.created_at || "").slice(0, 10)}</span>
                        </div>
                        <a href={`mailto:${msg.email}`} className="text-sm text-plum link-underline">{msg.email}</a>
                        <p className="text-sm text-matte/70 mt-2 whitespace-pre-line">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-editorial text-plum mb-4">Newsletter subscribers ({subscribers.length})</p>
                {subscribers.length === 0 ? <p className="text-matte/40 text-sm" data-testid="no-subscribers">No subscribers yet.</p> : (
                  <ul className="space-y-2">
                    {subscribers.map((sub) => (
                      <li key={sub.email} className="border-b border-matte/10 pb-2 flex justify-between items-baseline text-sm" data-testid={`subscriber-${sub.email}`}>
                        <a href={`mailto:${sub.email}`} className="text-matte/80 link-underline">{sub.email}</a>
                        <span className="text-[10px] uppercase tracking-wide-sm text-matte/40">{(sub.created_at || "").slice(0, 10)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </TabsContent>

          {/* PAGE PHOTOS */}
          <TabsContent value="sitephotos">
            <h2 className="font-serif text-2xl mb-2">Page Photos</h2>
            <p className="text-sm text-matte/50 mb-6 max-w-2xl">Replace the photos across the site. Upload a new image to change it instantly on the public site; delete it (the ✕) to restore the original. Use "Caption, alt &amp; position" for accessibility, focal point, and hero overlay.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {siteMedia.map((s) => <SiteMediaSlot key={s.key} slot={s} onSaved={refresh} />)}
            </div>
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders">
            <h2 className="font-serif text-2xl mb-6">Orders</h2>
            {orders.length === 0 ? <p className="text-matte/40">No orders yet.</p> : (
              <div className="grid gap-3">
                {orders.map((o) => (
                  <div key={o.order_id} className="border border-matte/10 p-5 bg-cream" data-testid={`admin-order-${o.order_id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-serif text-lg">{o.customer?.name || "Customer"} · {money(o.amount)}</p>
                        <p className="text-xs text-matte/50">{o.customer?.email} · {o.order_id.slice(0, 8)}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wide-sm text-sage">{o.status}</span>
                    </div>
                    <div className="mt-3 text-sm text-matte/60">
                      {(o.items || []).map((it, i) => <span key={`${it.title}-${i}`}>{it.title} ×{it.quantity}{i < o.items.length - 1 ? " · " : ""}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ENROLLMENTS */}
          <TabsContent value="enrollments">
            <h2 className="font-serif text-2xl mb-6">Enrollments</h2>
            {enrollments.length === 0 ? <p className="text-matte/40">No enrollments yet.</p> : (
              <div className="grid gap-3">
                {enrollments.map((e) => (
                  <div key={e.id} className="border border-matte/10 p-5 bg-cream" data-testid={`admin-enrollment-${e.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-serif text-lg">{e.child_name} <span className="text-sm text-matte/50">· {e.program_title}</span></p>
                        <p className="text-xs text-matte/50">Parent: {e.parent_name} · {e.email} · {e.phone}</p>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wide-sm ${e.status === "confirmed" ? "text-sage" : "text-wood"}`}>{e.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* DISCOUNTS */}
          <TabsContent value="discounts">
            <h2 className="font-serif text-2xl mb-6">Discount Codes</h2>
            <div className="flex flex-wrap gap-3 mb-8">
              <input placeholder="CODE" value={dcode} onChange={(e) => setDcode(e.target.value.toUpperCase())} className={`${inputCls} max-w-[200px]`} data-testid="discount-code-input" />
              <input type="number" placeholder="% off" value={dpct} onChange={(e) => setDpct(e.target.value)} className={`${inputCls} max-w-[120px]`} data-testid="discount-pct-input" />
              <button onClick={addDiscount} className="bg-matte text-ivory px-6 text-[11px] uppercase tracking-wide-sm hover:bg-plum transition-colors" data-testid="add-discount-btn">Create Code</button>
            </div>
            <div className="grid gap-3">
              {discounts.map((d) => (
                <div key={d.id} className="flex items-center justify-between border border-matte/10 p-4 bg-cream" data-testid={`admin-discount-${d.id}`}>
                  <p className="font-serif text-lg">{d.code} <span className="text-sm text-plum">— {d.percent_off}% off</span></p>
                  <button onClick={() => delDiscount(d.id)} className="text-matte/40 hover:text-destructive p-2"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
